import type { TFunction } from 'i18next';
import type { GraphNode, ProcessMapping } from '../../api/process-client/process-client.type';
import { Stages } from '../../api/process-client/process-client.type';
import { PhaseKey } from '../../helpers/phase-labels/phase-labels.type';
import type { ResolvedPhaseLabels } from '../../helpers/phase-labels/phase-labels.type';
import type { StatusLabelResolver } from '../../helpers/status-labels/status-labels.type';
import type { ProcessScope, ProcessWorkflow } from '../process/context/process.context.type';
import type { EventType } from '../process/event-graph/event-graph-client.type';
import { getEventGraphNodeLabel } from '../process/event-graph/event-graph.helpers';
import { ViewType } from '../process/process.type';

/**
 * Resolves which boards a process scope draws its statuses from.
 *
 * The process page can be scoped three ways, and only one of them populates
 * `scope.board`:
 * 1. By workflow (`useWorkflow`) — the workflow's own boards, which is the
 *    set `mapping.bf_to_customer` aggregates statuses across.
 * 2. By a single board.
 * 3. By team with no board chosen — every board on the team.
 *
 * Reading `scope.board` alone silently yields no boards in cases 1 and 3, which
 * leaves status labels unresolved and falls back to raw lowercase values.
 *
 * @param {ProcessScope} scope - The scope for one side of the process page
 * @returns {string[]} Subproject ids whose statuses this scope can render, empty when nothing is selected
 */
function getScopedSubprojectIds(scope: ProcessScope): string[] {
  if (scope.useWorkflow) {
    return scope.workflow?.subprojects ?? [];
  }
  if (scope.board) {
    return [scope.board.id];
  }
  return (scope.team?.subprojects ?? []).map((board) => board.id);
}

/**
 * Filters the process mapping by removing any BF stages that do not have customer stages.
 *
 * @param {ProcessMapping} mapping - The original process mapping.
 * @returns {ProcessMapping | null} - The filtered process mapping, otherwise null.
 */
const filterProcessMapping = (mapping?: ProcessMapping): ProcessMapping | null => {
  if (!mapping) {
    return null;
  }

  const filteredMapping = Object.keys(mapping.bf_to_customer).reduce((acc, stage) => {
    const customerStages = mapping.bf_to_customer[stage];

    if (customerStages.length > 0) {
      return {
        ...acc,
        [stage]: customerStages,
      };
    }

    return acc;
  }, {});

  return {
    ...mapping,
    bf_to_customer: filteredMapping,
  };
};

// `Stages` and `PhaseKey` are independent enums whose string values are the
// canonical phase keys. Mapping each `Stages` member to the corresponding
// `keyof ResolvedPhaseLabels` enforces compile-time parity — adding a new
// `Stages` member without updating this map is a TS error, eliminating the
// silent-undefined risk of the prior `as unknown as` double-cast.
const STAGE_TO_RESOLVED_LABEL_KEY: Record<Stages, PhaseKey> = {
  [Stages.Backlog]: PhaseKey.Backlog,
  [Stages.Blocked]: PhaseKey.Blocked,
  [Stages.InProgress]: PhaseKey.Wip,
  [Stages.InReview]: PhaseKey.Review,
  [Stages.InTest]: PhaseKey.Test,
  [Stages.Deployable]: PhaseKey.Deployable,
  [Stages.Done]: PhaseKey.Done,
};

/**
 * Returns the display title for a given canonical-keyed stage, preferring
 * the per-workflow `bucket_label_overrides` when supplied via
 * `resolvedLabels`, falling back to the i18n translation, then the raw
 * canonical key.
 *
 * @param {Stages} stage - The canonical-keyed stage (`backlog`, `wip`, …).
 * @param {TFunction} t - Translation function from `useTranslation()`.
 * @param {ResolvedPhaseLabels} [resolvedLabels] - Optional resolved per-
 *   workflow labels sourced from `external_section_map.labels`.
 * @returns {string} The display title for the stage.
 */
const getStageTitle = (stage: Stages, t: TFunction, resolvedLabels?: ResolvedPhaseLabels): string => {
  if (resolvedLabels) {
    const override = resolvedLabels[STAGE_TO_RESOLVED_LABEL_KEY[stage]];
    if (typeof override === 'string' && override.trim()) {
      return override;
    }
  }
  const titles = {
    [Stages.Backlog]: t('common.workflow_steps.backlog'),
    [Stages.Blocked]: t('common.workflow_steps.waiting'),
    [Stages.InProgress]: t('common.workflow_steps.in_progress'),
    [Stages.InReview]: t('common.workflow_steps.in_review'),
    [Stages.InTest]: t('common.workflow_steps.in_test'),
    [Stages.Deployable]: t('common.workflow_steps.deployable'),
    [Stages.Done]: t('common.workflow_steps.done'),
  };

  return titles[stage] ?? stage;
};

/**
 * Returns the title of a given view type.
 *
 * @param {ViewType} viewType - The view type for which to retrieve the title.
 * @returns {string} The title of the view type.
 */
const getViewTypeTitle = (viewType: ViewType): string => {
  const titles = {
    [ViewType.Count]: 'Count',
    [ViewType.Percentage]: 'Percentage',
    [ViewType.CycleTime]: 'Cycle Time (days)',
    [ViewType.Cost]: 'Cost',
  };

  return titles[viewType];
};

/**
 * Returns an array of the bloomfilter stages.
 *
 * @param {ProcessMapping} mapping - The process mapping.
 * @returns {Array} An array of the bloomfilter stages.
 */
const getStages = (mapping: ProcessMapping): Stages[] => {
  return Object.values(Stages).filter((stage: Stages) => stage in mapping.bf_to_customer);
};

/**
 * Returns the index of a stage in the stages array.
 *
 * @param {Stages} stage - The stage to find the index of.
 * @param {ProcessMapping} mapping - The process mapping.
 * @returns {number} The index of the stage in the stages array.
 */
const getStageIndex = (stage: Stages, mapping: ProcessMapping): number => getStages(mapping).indexOf(stage);

/**
 * Finds a graph node by a specific order.
 *
 * @param {GraphNode[]} nodes - An array of graph nodes to search through.
 * @param {number} order - The order value to match.
 * @returns {GraphNode | undefined} - The graph node with the specified order, or undefined if not found.
 */
const findByOrder = (nodes: GraphNode[], order: number): GraphNode | undefined =>
  nodes.find((node) => node.order === order);

/**
 * Finds a graph node by a specific name.
 *
 * @param {GraphNode[]} nodes - The array of GraphNodes to search through.
 * @param {string} name - The name of the node to find.
 * @returns {GraphNode | undefined} - The graph node with the specified name, or undefined if not found.
 */
const findByName = (nodes: GraphNode[], name: string): GraphNode | undefined =>
  nodes.find((node) => node.name === name);

/**
 * Determines if the given array of stages represents a complex view.
 *
 * @param {Stages[]} stages - The array of stages to check.
 * @param {ProcessMapping} mapping - The process mapping.
 * @returns {boolean} Returns true if the array of stages represents a complex view, otherwise false.
 */
const isComplexView = (stages: Stages[], mapping: ProcessMapping): boolean => {
  if (!stages.length) {
    return false;
  }

  const referenceStages = getStages(mapping);

  return referenceStages.length === stages.length && referenceStages.every((stage) => stages.includes(stage));
};

/**
 * Returns the title of a given workflow.
 *
 * @param {ProcessWorkflow | null} workflow - The workflow for which to retrieve the title.
 * @param {boolean} isRightGraph - Whether the workflow is on the right graph.
 * @param {boolean} isComparisonView - Whether the workflow is in a comparison view.
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {string} The title of the workflow.
 */
function getProcessWorkflowTaskDetailTitle(
  workflow: ProcessWorkflow | null,
  isRightGraph: boolean,
  isComparisonView: boolean,
  t: TFunction,
  resolvedLabels?: ResolvedPhaseLabels,
): string {
  if (!workflow) {
    return '';
  }
  const { source, destination, status } = workflow;

  return `${isComparisonView ? (isRightGraph ? 'Right' : 'Left') + ' Graph: ' : ''}${status} from ${getStageTitle(source, t, resolvedLabels)} to ${getStageTitle(destination, t, resolvedLabels)}`;
}

/**
 * Returns the title of a given event edge.
 *
 * @param {string} edgeId - The ID of the event edge.
 * @param {boolean} isRightGraph - Whether the event edge is on the right graph.
 * @param {boolean} isComparisonView - Whether this is in a comparison view.
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @param {StatusLabelResolver} [resolveStatusLabelForDisplay] - Maps a raw status to its
 *   source-cased label. Must be the resolver for the board the edge belongs to,
 *   so this title matches the node label the user clicked.
 * @returns {string} The title of the event edge.
 */
function getEventEdgeTaskDetailTitle(
  edgeId: string,
  isRightGraph: boolean,
  isComparisonView: boolean,
  t: TFunction,
  resolveStatusLabelForDisplay?: StatusLabelResolver,
): string {
  const [source, destination] = edgeId.split(',');

  const sourceString = getEventGraphNodeLabel(
    {
      id: source,
      event_type: source.split('_')[0] as EventType,
      sequence: 0,
      isRightGraph,
    },
    t,
    resolveStatusLabelForDisplay,
  );
  const destinationString = getEventGraphNodeLabel(
    {
      id: destination,
      event_type: destination.split('_')[0] as EventType,
      sequence: 0,
      isRightGraph,
    },
    t,
    resolveStatusLabelForDisplay,
  );

  return `${isComparisonView ? (isRightGraph ? 'Right' : 'Left') + ' Graph: ' : ''}${sourceString} - ${destinationString}`;
}

export {
  filterProcessMapping,
  findByName,
  findByOrder,
  getEventEdgeTaskDetailTitle,
  getProcessWorkflowTaskDetailTitle,
  getScopedSubprojectIds,
  getStageIndex,
  getStages,
  getStageTitle,
  getViewTypeTitle,
  isComplexView,
};
